/**
 * Tabulator Entity Manager - Integrated with all 15 workflow types
 * Features comprehensive search, filtering, and CRUD operations
 */

import {
    Alert,
    Badge,
    Box,
    Button,
    Card,
    Grid,
    Group,
    Modal,
    ScrollArea,
    Stack,
    Text,
    Textarea,
    TextInput,
    Title
} from '@mantine/core';
import {
    IconDatabase,
    IconDownload,
    IconFilter,
    IconPlus,
    IconRefresh,
    IconSearch
} from '@tabler/icons-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

// Apollo Client imports
// eslint-disable-next-line no-restricted-imports -- DATA-LAYER component, requires direct Apollo Client access
import { ApolloClient, createHttpLink, gql, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';

// Import Tabulator types and CSS
import { TabulatorFull } from 'tabulator-tables';
import 'tabulator-tables/dist/css/tabulator_midnight.min.css';

// Import all 15 workflow types and composition hook
import { WORKFLOW_TYPES, WorkflowType } from '../types-16-complete';
// Auto-column detection: surfaces backend fields not in hardcoded column lists
import { autoColumnsFromDraft } from '../utils/tabulator-auto-columns';

// Using composition pattern instead of direct Apollo Client usage

interface EntityConfig {
    title: string;
    icon: string;
    count?: number;
    columns: Array<{
        title: string;
        field: string;
        width?: number;
        formatter?: string | ((cell: any, formatterParams: any) => string);
        hozAlign?: 'left' | 'center' | 'right';
        headerSort?: boolean;
    }>;
}

interface EntityManagerProps {
    onNavigateToType?: (type: WorkflowType) => void;
    onEntityAction?: (action: string, type: WorkflowType, entity: any) => void;
}

/**
 * Complete Entity Configurations for all 15 workflow types
 */
const ENTITY_CONFIGURATIONS: Record<WorkflowType, EntityConfig> = {
    WorkflowDefinition: {
        title: 'Workflow Definitions',
        icon: '🔀',
        columns: [
            { title: 'Key', field: 'key', width: 200 },
            { title: 'Name', field: 'draft.name', width: 200 },
            { title: 'Version', field: 'draft.version', width: 100 },
            { title: 'Initial State', field: 'draft.initial_state', width: 150 },
            { title: 'States Count', field: 'draft.states', width: 120, formatter: (cell: any) => String(Object.keys(cell.getValue() || {}).length) },
            { title: 'Updated By', field: 'updatedBy', width: 150 },
            { title: 'Updated', field: 'updatedMs', width: 150, formatter: 'datetime' },
            { title: 'Actions', field: 'actions', formatter: 'actionsFormatter', width: 150, hozAlign: 'center', headerSort: false }
        ]
    },
    ActorDescriptor: {
        title: 'Actor Descriptors',
        icon: '👥',
        columns: [
            { title: 'Key', field: 'key', width: 200 },
            { title: 'Name', field: 'draft.name', width: 200 },
            { title: 'Kind', field: 'draft.kind', width: 120 },
            { title: 'Roles', field: 'draft.roles', width: 200, formatter: (cell: any) => Array.isArray(cell.getValue()) ? cell.getValue().join(', ') : '' },
            { title: 'Updated By', field: 'updatedBy', width: 150 },
            { title: 'Updated', field: 'updatedMs', width: 150, formatter: 'datetime' },
            { title: 'Actions', field: 'actions', formatter: 'actionsFormatter', width: 150, hozAlign: 'center', headerSort: false }
        ]
    },
    TransitionCatalog: {
        title: 'Transition Catalogs',
        icon: '📋',
        columns: [
            { title: 'Key', field: 'key', width: 200 },
            { title: 'Transitions', field: 'draft.transitions', width: 200, formatter: (cell: any) => Array.isArray(cell.getValue()) ? cell.getValue().length + ' items' : '0 items' },
            { title: 'Guards', field: 'draft.guards', width: 150, formatter: (cell: any) => Array.isArray(cell.getValue()) ? cell.getValue().length + ' guards' : '0 guards' },
            { title: 'Actions', field: 'draft.actions', width: 150, formatter: (cell: any) => Array.isArray(cell.getValue()) ? cell.getValue().length + ' actions' : '0 actions' },
            { title: 'Updated', field: 'updatedMs', width: 150, formatter: 'datetime' },
            { title: 'Actions', field: 'actions', formatter: 'actionsFormatter', width: 150, hozAlign: 'center', headerSort: false }
        ]
    },
    Instance: {
        title: 'Workflow Instances',
        icon: '⚡',
        columns: [
            { title: 'ID', field: 'id', width: 200 },
            { title: 'State', field: 'draft.state', width: 150 },
            { title: 'Data Keys', field: 'draft.data', width: 150, formatter: (cell: any) => Object.keys(cell.getValue() || {}).length + ' keys' },
            { title: 'Updated', field: 'updatedMs', width: 150, formatter: 'datetime' },
            { title: 'Actions', field: 'actions', formatter: 'actionsFormatter', width: 150, hozAlign: 'center', headerSort: false }
        ]
    },
    Signal: {
        title: 'Workflow Signals',
        icon: '📡',
        columns: [
            { title: 'Key', field: 'key', width: 200 },
            { title: 'Name', field: 'draft.name', width: 200 },
            { title: 'Payload Type', field: 'draft.payload', width: 150, formatter: (cell: any) => typeof cell.getValue() },
            { title: 'Updated', field: 'updatedMs', width: 150, formatter: 'datetime' },
            { title: 'Actions', field: 'actions', formatter: 'actionsFormatter', width: 150, hozAlign: 'center', headerSort: false }
        ]
    },
    ExecutionResult: {
        title: 'Execution Results',
        icon: '📊',
        columns: [
            { title: 'Key', field: 'key', width: 200 },
            { title: 'Success', field: 'draft.success', width: 100, formatter: 'tickCross' },
            { title: 'Duration (ms)', field: 'draft.duration_ms', width: 120 },
            { title: 'Safety Score', field: 'draft.safetyScore', width: 120 },
            { title: 'Risk Level', field: 'draft.riskLevel', width: 120 },
            { title: 'Updated', field: 'updatedMs', width: 150, formatter: 'datetime' },
            { title: 'Actions', field: 'actions', formatter: 'actionsFormatter', width: 150, hozAlign: 'center', headerSort: false }
        ]
    },
    TimelineItem: {
        title: 'Timeline Items',
        icon: '⏰',
        columns: [
            { title: 'Key', field: 'key', width: 200 },
            { title: 'ExecutionResult', field: 'draft.executionresult', width: 200 },
            { title: 'Timestamp', field: 'draft.timestamp', width: 180, formatter: 'datetime' },
            { title: 'ActorDescriptor', field: 'draft.actordescriptor', width: 150 },
            { title: 'Updated', field: 'updatedMs', width: 150, formatter: 'datetime' },
            { title: 'Actions', field: 'actions', formatter: 'actionsFormatter', width: 150, hozAlign: 'center', headerSort: false }
        ]
    },
    Task: {
        title: 'Workflow Tasks',
        icon: '✅',
        columns: [
            { title: 'Key', field: 'key', width: 200 },
            { title: 'Name', field: 'draft.name', width: 200 },
            { title: 'Status', field: 'draft.status', width: 120 },
            { title: 'Assignee', field: 'draft.assignee', width: 150 },
            { title: 'Priority', field: 'draft.priority', width: 100 },
            { title: 'Due Date', field: 'draft.due_date', width: 150, formatter: 'datetime' },
            { title: 'Updated', field: 'updatedMs', width: 150, formatter: 'datetime' },
            { title: 'Actions', field: 'actions', formatter: 'actionsFormatter', width: 150, hozAlign: 'center', headerSort: false }
        ]
    },
    JobStatus: {
        title: 'Job Status',
        icon: '🔄',
        columns: [
            { title: 'Key', field: 'key', width: 200 },
            { title: 'Status', field: 'draft.status', width: 120 },
            { title: 'Progress', field: 'draft.progress', width: 100, formatter: 'progress' },
            { title: 'Started', field: 'draft.started_at', width: 150, formatter: 'datetime' },
            { title: 'Completed', field: 'draft.completed_at', width: 150, formatter: 'datetime' },
            { title: 'Updated', field: 'updatedMs', width: 150, formatter: 'datetime' },
            { title: 'Actions', field: 'actions', formatter: 'actionsFormatter', width: 150, hozAlign: 'center', headerSort: false }
        ]
    },
    Policy: {
        title: 'Governance Policies',
        icon: '📜',
        columns: [
            { title: 'Key', field: 'key', width: 200 },
            { title: 'Name', field: 'draft.name', width: 200 },
            { title: 'Version', field: 'draft.version', width: 100 },
            { title: 'Active', field: 'draft.active', width: 100, formatter: 'tickCross' },
            { title: 'Rules Count', field: 'draft.rules', width: 120, formatter: (cell: any) => Array.isArray(cell.getValue()) ? cell.getValue().length + ' rules' : '0 rules' },
            { title: 'Updated', field: 'updatedMs', width: 150, formatter: 'datetime' },
            { title: 'Actions', field: 'actions', formatter: 'actionsFormatter', width: 150, hozAlign: 'center', headerSort: false }
        ]
    },
    Context: {
        title: 'Execution Contexts',
        icon: '🌐',
        columns: [
            { title: 'Key', field: 'key', width: 200 },
            { title: 'Environment', field: 'draft.environment', width: 150 },
            { title: 'Variables', field: 'draft.variables', width: 150, formatter: (cell: any) => Object.keys(cell.getValue() || {}).length + ' vars' },
            { title: 'Tenant', field: 'draft.tenant', width: 120 },
            { title: 'Updated', field: 'updatedMs', width: 150, formatter: 'datetime' },
            { title: 'Actions', field: 'actions', formatter: 'actionsFormatter', width: 150, hozAlign: 'center', headerSort: false }
        ]
    },
    Schedule: {
        title: 'Workflow Schedules',
        icon: '📅',
        columns: [
            { title: 'Key', field: 'key', width: 200 },
            { title: 'Name', field: 'draft.name', width: 200 },
            { title: 'Cron Pattern', field: 'draft.cron', width: 150 },
            { title: 'Active', field: 'draft.active', width: 100, formatter: 'tickCross' },
            { title: 'Next Run', field: 'draft.next_run', width: 180, formatter: 'datetime' },
            { title: 'Updated', field: 'updatedMs', width: 150, formatter: 'datetime' },
            { title: 'Actions', field: 'actions', formatter: 'actionsFormatter', width: 150, hozAlign: 'center', headerSort: false }
        ]
    },
    Resource: {
        title: 'System Resources',
        icon: '💾',
        columns: [
            { title: 'Key', field: 'key', width: 200 },
            { title: 'Name', field: 'draft.name', width: 200 },
            { title: 'Type', field: 'draft.type', width: 120 },
            { title: 'Capacity', field: 'draft.capacity', width: 100 },
            { title: 'Available', field: 'draft.available', width: 100 },
            { title: 'Usage %', field: 'draft.usage_percent', width: 100, formatter: 'progress' },
            { title: 'Updated', field: 'updatedMs', width: 150, formatter: 'datetime' },
            { title: 'Actions', field: 'actions', formatter: 'actionsFormatter', width: 150, hozAlign: 'center', headerSort: false }
        ]
    },
    Integration: {
        title: 'System Integrations',
        icon: '🔗',
        columns: [
            { title: 'Key', field: 'key', width: 200 },
            { title: 'Name', field: 'draft.name', width: 200 },
            { title: 'Type', field: 'draft.type', width: 120 },
            { title: 'Status', field: 'draft.status', width: 120 },
            { title: 'Health', field: 'draft.health_check', width: 100, formatter: 'tickCross' },
            { title: 'Last Sync', field: 'draft.last_sync', width: 180, formatter: 'datetime' },
            { title: 'Updated', field: 'updatedMs', width: 150, formatter: 'datetime' },
            { title: 'Actions', field: 'actions', formatter: 'actionsFormatter', width: 150, hozAlign: 'center', headerSort: false }
        ]
    },
    Escalation: {
        title: 'Escalation Paths',
        icon: '🚨',
        columns: [
            { title: 'Key', field: 'key', width: 200 },
            { title: 'Name', field: 'draft.name', width: 200 },
            { title: 'Trigger', field: 'draft.trigger_condition', width: 180 },
            { title: 'Timeout (ms)', field: 'draft.timeout_ms', width: 120 },
            { title: 'Auto Reassign', field: 'draft.auto_reassign', width: 120, formatter: 'tickCross' },
            { title: 'Path Length', field: 'draft.escalation_path', width: 120, formatter: (cell: any) => Array.isArray(cell.getValue()) ? cell.getValue().length + ' steps' : '0 steps' },
            { title: 'Updated', field: 'updatedMs', width: 150, formatter: 'datetime' },
            { title: 'Actions', field: 'actions', formatter: 'actionsFormatter', width: 150, hozAlign: 'center', headerSort: false }
        ]
    },
    OptimizationRecommendation: {
        title: 'Optimization Recommendations',
        icon: '🚀',
        columns: [
            { title: 'Key', field: 'key', width: 200 },
            { title: 'Type', field: 'draft.recommendation_type', width: 150 },
            { title: 'Workflow Type', field: 'draft.workflow_type', width: 150 },
            { title: 'Priority', field: 'draft.priority', width: 100 },
            { title: 'Confidence', field: 'draft.confidence_score', width: 100, formatter: (cell: any) => `${(cell.getValue() * 100).toFixed(1)}%` },
            { title: 'Savings (USD)', field: 'draft.estimated_impact.cost_impact.annual_savings_usd', width: 120, formatter: (cell: any) => `$${cell.getValue()?.toLocaleString() || '0'}` },
            { title: 'Updated', field: 'updatedMs', width: 150, formatter: 'datetime' },
            { title: 'Actions', field: 'actions', formatter: 'actionsFormatter', width: 150, hozAlign: 'center', headerSort: false }
        ]
    }
};

export const TabulatorEntityManager: React.FC<EntityManagerProps> = ({
    onNavigateToType,
    onEntityAction
}) => {
    // State management
    const [selectedEntity, setSelectedEntity] = useState<WorkflowType>('WorkflowDefinition');
    const [searchTerm, setSearchTerm] = useState('');
    const [tableSearchTerm, setTableSearchTerm] = useState('');
    const [entityCounts, setEntityCounts] = useState<Record<WorkflowType, number>>({} as any);
    const [loading, setLoading] = useState(false);
    const [modalOpened, setModalOpened] = useState(false);
    const [modalMode, setModalMode] = useState<'view' | 'edit' | 'create'>('view');
    const [modalData, setModalData] = useState<any>(null);
    
    // Tabulator reference
    const tableRef = useRef<HTMLDivElement>(null);
    const tabulatorInstance = useRef<InstanceType<typeof TabulatorFull> | null>(null);

    // Apollo GraphQL client (module-local singleton)
    let __apolloClient: ApolloClient | null = null;
    function getTenant(): string {
        try { return (globalThis as any).__TENANT__ || sessionStorage.getItem('tenant') || 'test'; } catch { return 'test'; }
    }
    function getAuthToken(): string | undefined {
        try { return (globalThis as any).__AUTH_TOKEN__ as string | undefined; } catch { return undefined; }
    }
    function getGraphqlUrl(): string {
        try { return (globalThis as any).__GQL_URL__ || '/graphql'; } catch { return '/graphql'; }
    }
    function getApolloClient(): ApolloClient {
        if (__apolloClient) return __apolloClient;
        const httpLink = createHttpLink({ uri: getGraphqlUrl() });
        const authLink = setContext((_: any, { headers }) => {
            const token = getAuthToken();
            return {
                headers: {
                    ...headers,
                    authorization: token ? token : '',
                    'x-tenant': getTenant(),
                    'content-type': 'application/json'
                }
            };
        });
        __apolloClient = new ApolloClient({ link: authLink.concat(httpLink), cache: new InMemoryCache() });
        return __apolloClient;
    }

    // Load entity counts
    const loadEntityCounts = useCallback(async () => {
        const counts: Record<string, number> = {};
        
        const client = getApolloClient();
        for (const workflowType of Object.keys(WORKFLOW_TYPES) as WorkflowType[]) {
            try {
                const { data } = await client.query<Record<string, any>>({
                    query: gql`
                        query ListDrafts($input: ListDraftsInput!) {
                            listDrafts(input: $input) { key }
                        }
                    `,
                    variables: { input: { tenant: getTenant(), kind: workflowType } },
                    fetchPolicy: 'no-cache'
                });
                counts[workflowType] = data?.listDrafts?.length || 0;
            } catch (error) {
                console.error(`Failed to load count for ${workflowType}:`, error);
                counts[workflowType] = 0;
            }
        }
        
        setEntityCounts(counts as any);
    }, []);

    // Load entity data for selected type
    const loadEntityData = useCallback(async (entityType: WorkflowType) => {
        setLoading(true);
        
        try {
            const client = getApolloClient();
            const listRes = await client.query<Record<string, any>>({
                query: gql`
                    query ListDrafts($input: ListDraftsInput!) {
                        listDrafts(input: $input) {
                            key
                            updatedBy
                            updatedMs
                            deleted
                        }
                    }
                `,
                variables: { input: { tenant: getTenant(), kind: entityType } },
                fetchPolicy: 'no-cache'
            });
            const drafts = (listRes.data?.listDrafts || []) as Array<{ key: string }>;
            const fullData = await Promise.all(drafts.map(async (item) => {
                const { data } = await client.query<Record<string, any>>({
                    query: gql`
                        query GetDraft($input: GetDraftInput!) {
                            getDraft(input: $input) { key draft }
                        }
                    `,
                    variables: { input: { tenant: getTenant(), kind: entityType, key: item.key } },
                    fetchPolicy: 'no-cache'
                });
                return { ...item, draft: data?.getDraft?.draft || {} } as { key: string; draft: Record<string, unknown> };
            }));

            // Update Tabulator — extend columns with any backend fields not in config
            if (tabulatorInstance.current && fullData.length > 0) {
                const config = ENTITY_CONFIGURATIONS[entityType];
                const knownFields = new Set(config.columns.map((c) => c.field));
                const actionsCol = config.columns.find((c) => c.field === 'actions');
                const baseColumns = config.columns
                    .filter((c) => c.field !== 'actions')
                    .map((col) => ({ ...col, formatter: col.formatter === 'actionsFormatter' ? undefined : col.formatter }));
                const extraColumns = autoColumnsFromDraft(fullData, knownFields);
                const actionsColDef = actionsCol ? [{
                    ...actionsCol,
                    formatter: (cell: any) => `
                        <div style="display:flex;gap:4px;justify-content:center">
                            <button onclick="window.viewEntity('${cell.getRow().getData().key}')" style="background:#228be6;color:#fff;border:none;padding:4px 8px;border-radius:4px;cursor:pointer">👁️</button>
                            <button onclick="window.editEntity('${cell.getRow().getData().key}')" style="background:#fd7e14;color:#fff;border:none;padding:4px 8px;border-radius:4px;cursor:pointer">✏️</button>
                            <button onclick="window.deleteEntity('${cell.getRow().getData().key}')" style="background:#fa5252;color:#fff;border:none;padding:4px 8px;border-radius:4px;cursor:pointer">🗑️</button>
                        </div>`
                }] : [];
                tabulatorInstance.current.setColumns([...baseColumns, ...extraColumns, ...actionsColDef] as any);
                tabulatorInstance.current.setData(fullData);
            } else if (tabulatorInstance.current) {
                tabulatorInstance.current.setData(fullData);
            }

        } catch (error) {
            console.error('Failed to load entity data:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Initialize Tabulator
    const initializeTabulator = useCallback((entityType: WorkflowType) => {
        if (!tableRef.current) return;

        // Destroy existing table
        if (tabulatorInstance.current) {
            tabulatorInstance.current.destroy();
        }

        const config = ENTITY_CONFIGURATIONS[entityType];
        
        // Create Tabulator instance
        tabulatorInstance.current = new TabulatorFull(tableRef.current, {
            data: [],
            columns: config.columns.map(col => ({
                ...col,
                formatter: col.formatter === 'actionsFormatter' ? (cell: any) => {
                    return `
                        <div style="display: flex; gap: 4px; justify-content: center;">
                            <button onclick="window.viewEntity('${cell.getRow().getData().key}')" style="background: #228be6; color: white; border: none; padding: 4px 8px; border-radius: 4px; cursor: pointer;" title="View">👁️</button>
                            <button onclick="window.editEntity('${cell.getRow().getData().key}')" style="background: #fd7e14; color: white; border: none; padding: 4px 8px; border-radius: 4px; cursor: pointer;" title="Edit">✏️</button>
                            <button onclick="window.deleteEntity('${cell.getRow().getData().key}')" style="background: #fa5252; color: white; border: none; padding: 4px 8px; border-radius: 4px; cursor: pointer;" title="Delete">🗑️</button>
                        </div>
                    `;
                } : col.formatter
            })),
            layout: 'fitColumns',
            responsiveLayout: 'hide',
            pagination: 'local',
            paginationSize: 20,
            paginationSizeSelector: [10, 20, 50, 100],
            movableColumns: true,
            resizableRows: true,
            selectable: true,
            height: '500px',
            placeholder: 'No data available'
        });

        // Set up global functions for actions
        (window as any).viewEntity = (key: string) => {
            const row = tabulatorInstance.current?.getRows().find((r) => r.getData().key === key);
            if (row) {
                setModalData(row.getData());
                setModalMode('view');
                setModalOpened(true);
            }
        };

        (window as any).editEntity = (key: string) => {
            const row = tabulatorInstance.current?.getRows().find((r) => r.getData().key === key);
            if (row) {
                setModalData(row.getData());
                setModalMode('edit');
                setModalOpened(true);
            }
        };

        (window as any).deleteEntity = async (key: string) => {
            if (!confirm(`Are you sure you want to delete ${key}?`)) return;
            
            try {
                const client = getApolloClient();
                const { data } = await client.mutate<Record<string, any>>({
                    mutation: gql`
                        mutation DeleteDraft($input: DeleteDraftInput!) {
                            deleteDraft(input: $input)
                        }
                    `,
                    variables: { input: { tenant: getTenant(), kind: entityType, key } }
                });
                if (data?.deleteDraft) {
                    loadEntityData(entityType);
                    loadEntityCounts();
                    onEntityAction?.('delete', entityType, { key });
                }
            } catch (error) {
                console.error('Failed to delete entity:', error);
                alert('Failed to delete entity');
            }
        };

    }, [loadEntityData, loadEntityCounts, onEntityAction]);

    // Handle entity selection
    const handleEntitySelect = useCallback((entityType: WorkflowType) => {
        setSelectedEntity(entityType);
        initializeTabulator(entityType);
        loadEntityData(entityType);
    }, [initializeTabulator, loadEntityData]);

    // Handle table search
    const handleTableSearch = useCallback((term: string) => {
        if (tabulatorInstance.current && term) {
            tabulatorInstance.current.setFilter((data: Record<string, unknown>) => {
                const searchValue = term.toLowerCase();
                return Object.values(data).some((value: unknown) => {
                    if (value && typeof value === 'object') {
                        return JSON.stringify(value).toLowerCase().includes(searchValue);
                    }
                    return String(value).toLowerCase().includes(searchValue);
                });
            });
        } else if (tabulatorInstance.current) {
            tabulatorInstance.current.clearFilter();
        }
    }, []);

    // Handle modal save
    const handleModalSave = async () => {
        if (!modalData) return;
        
        try {
            const key = modalData.key || `new-${Date.now()}`;
            const client = getApolloClient();
            const { data } = await client.mutate<Record<string, any>>({
                mutation: gql`
                    mutation SaveDraft($input: SaveDraftInput!) {
                        saveDraft(input: $input) { ok }
                    }
                `,
                variables: { input: { tenant: getTenant(), kind: selectedEntity, key, draft: modalData.draft } }
            });
            if (data?.saveDraft?.ok) {
                setModalOpened(false);
                loadEntityData(selectedEntity);
                loadEntityCounts();
                onEntityAction?.('save', selectedEntity, modalData);
            } else {
                alert('Failed to save entity');
            }
        } catch (error) {
            console.error('Failed to save entity:', error);
            alert('Failed to save entity');
        }
    };

    // Initialize on mount
    useEffect(() => {
        loadEntityCounts();
        initializeTabulator(selectedEntity);
        loadEntityData(selectedEntity);
    }, []);

    // Filter entities based on search
    const filteredEntityTypes = (Object.keys(WORKFLOW_TYPES) as WorkflowType[]).filter(type => {
        const config = ENTITY_CONFIGURATIONS[type];
        return config.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
               type.toLowerCase().includes(searchTerm.toLowerCase());
    });

    return (
        <Card shadow="sm" padding="lg" radius="md" withBorder style={{ height: '100%' }}>
            <Group justify="space-between" mb="md">
                <Title order={2}>
                    <Group>
                        <IconDatabase size={24} />
                        <Text>Entity Manager</Text>
                        <Badge variant="light" size="sm">
                            All 15 Workflow Types
                        </Badge>
                    </Group>
                </Title>
                
                <Group>
                    <Button
                        leftSection={<IconRefresh size={16} />}
                        variant="light"
                        size="sm"
                        onClick={() => {
                            loadEntityData(selectedEntity);
                            loadEntityCounts();
                        }}
                        loading={loading}
                    >
                        Refresh
                    </Button>
                    <Button
                        leftSection={<IconDownload size={16} />}
                        variant="light"
                        size="sm"
                        onClick={() => {
                            if (tabulatorInstance.current) {
                                tabulatorInstance.current.download('csv', `${selectedEntity}-export.csv`);
                            }
                        }}
                    >
                        Export
                    </Button>
                </Group>
            </Group>

            <Grid>
                {/* Entity Type Selector */}
                <Grid.Col span={3}>
                    <Stack gap="xs">
                        <TextInput
                            placeholder="Search entity types..."
                            leftSection={<IconSearch size={16} />}
                            value={searchTerm}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.currentTarget.value)}
                            size="sm"
                        />
                        
                        <ScrollArea h={400}>
                            <Stack gap={4}>
                                {filteredEntityTypes.map((entityType) => {
                                    const config = ENTITY_CONFIGURATIONS[entityType];
                                    return (
                                        <Button
                                            key={entityType}
                                            variant={selectedEntity === entityType ? 'filled' : 'subtle'}
                                            justify="space-between"
                                            fullWidth
                                            size="sm"
                                            onClick={() => handleEntitySelect(entityType)}
                                            leftSection={<Text size="lg">{config.icon}</Text>}
                                            rightSection={
                                                <Badge size="xs" variant="light">
                                                    {entityCounts[entityType] || 0}
                                                </Badge>
                                            }
                                        >
                                            <Box style={{ textAlign: 'left' }}>
                                                <Text size="sm" fw={500} truncate>
                                                    {config.title}
                                                </Text>
                                            </Box>
                                        </Button>
                                    );
                                })}
                            </Stack>
                        </ScrollArea>
                    </Stack>
                </Grid.Col>

                {/* Main Table Area */}
                <Grid.Col span={9}>
                    <Stack gap="md">
                        <Group justify="space-between">
                            <Group>
                                <Text size="xl" fw={600}>
                                    {ENTITY_CONFIGURATIONS[selectedEntity].icon} {ENTITY_CONFIGURATIONS[selectedEntity].title}
                                </Text>
                                <Badge variant="outline">
                                    {entityCounts[selectedEntity] || 0} items
                                </Badge>
                            </Group>
                            
                            <Group>
                                <TextInput
                                    placeholder="Search in table..."
                                    leftSection={<IconFilter size={16} />}
                                    value={tableSearchTerm}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        const value = e.currentTarget.value;
                                        setTableSearchTerm(value);
                                        handleTableSearch(value);
                                    }}
                                    size="sm"
                                    w={200}
                                />
                                <Button
                                    leftSection={<IconPlus size={16} />}
                                    size="sm"
                                    onClick={() => {
                                        setModalData({ key: '', draft: {} });
                                        setModalMode('create');
                                        setModalOpened(true);
                                    }}
                                >
                                    Create New
                                </Button>
                            </Group>
                        </Group>

                        <Box>
                            <div ref={tableRef} />
                            {loading && (
                                <Alert color="blue" mt="md">
                                    <Text>Loading {ENTITY_CONFIGURATIONS[selectedEntity].title}...</Text>
                                </Alert>
                            )}
                        </Box>
                    </Stack>
                </Grid.Col>
            </Grid>

            {/* Entity Modal */}
            <Modal
                opened={modalOpened}
                onClose={() => setModalOpened(false)}
                title={`${modalMode === 'create' ? 'Create' : modalMode === 'edit' ? 'Edit' : 'View'} ${ENTITY_CONFIGURATIONS[selectedEntity].title}`}
                size="lg"
            >
                {modalData && (
                    <Stack gap="md">
                        <TextInput
                            label="Key"
                            value={modalData.key || ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setModalData({ ...modalData, key: e.currentTarget.value })}
                            disabled={modalMode === 'view' || (modalMode === 'edit' && modalData.key)}
                        />
                        <Textarea
                            label="Draft Data (JSON)"
                            value={JSON.stringify(modalData.draft || {}, null, 2)}
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                                try {
                                    const draft = JSON.parse(e.currentTarget.value);
                                    setModalData({ ...modalData, draft });
                                } catch (error) {
                                    // Invalid JSON, don't update
                                }
                            }}
                            disabled={modalMode === 'view'}
                            minRows={10}
                            maxRows={20}
                            autosize
                            styles={{
                                input: {
                                    fontFamily: 'Monaco, Menlo, monospace',
                                    fontSize: '12px'
                                }
                            }}
                        />
                        
                        {modalMode !== 'view' && (
                            <Group justify="flex-end">
                                <Button variant="default" onClick={() => setModalOpened(false)}>
                                    Cancel
                                </Button>
                                <Button onClick={handleModalSave}>
                                    {modalMode === 'create' ? 'Create' : 'Save'}
                                </Button>
                            </Group>
                        )}
                    </Stack>
                )}
            </Modal>
        </Card>
    );
};

export default TabulatorEntityManager;